/** @jsx jsx */
import React, { useContext, useCallback, useRef } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { useSpring, animated as a } from "react-spring"
import { Flex, Button, Divider, Box, Text, jsx } from "theme-ui"
import Image from "gatsby-image"

import { Context } from "../../context/MainContext"
import { ShopifyContext } from "../../context/ShopifyContext"

import useClickObserver from "../../hooks/useClickObserver"
import useEventListener from "../../hooks/useEventListener"
import { unique } from "../../util"

const styles = {
  container: {
    height: "100%",
    minHeight: "100vh",
    minHeight: "-webkit-fill-available",
    zIndex: 50,
    padding: ["20px 8px", null, "20px"],
    width: "100%",
    maxWidth: "612px",
    position: "fixed",
    background: "#333",
    display: "flex",
    flexDirection: "column",
    top: "0px",
    right: "0px",
    transform: "translate3d(100%,0,0)",
  },
  header: {
    fontSize: "50px",
    padding: "0px",
    color: "#FFF",
    fontFamily: "body",
    display: "flex",
    justifyContent: "center",
    h2: {
      fontSize: "inherit",
    },
    button: {
      fontFamily: "secondary",
      position: "absolute",
      background: "transparent",
      top: "40px",
      right: "20px",
      fontSize: "18px",
      border: "none",
      padding: "2px",
    },
  },
  cartContent: {
    flexDirection: "column",
    overflowY: "auto",
  },
  bundle: {
    color: "#FFF",
    fontSize: ["14px", null, "18px"],
    fontWeight: "normal",
    padding: ["20px 0px", null, "40px 0px"],
    width: "100%",
    "h3, p": {
      fontWeight: "normal",
      fontSize: "inherit",
    },
  },
  bundleDetails: {
    flexDirection: "column",
    paddingLeft: ["10px", null, "20px"],
    button: {
      background: "transparent",
      fontSize: "inherit",
      color: "inherit",
      margin: "0px",
      padding: "0px",
    },
  },
  bundleList: {
    display: "flex",
    flexDirection: "column",
    flexGrow: "1",
    color: "inherit",
    opacity: "0.5",
    listStyle: "none",
    padding: "0px",
  },
  note: {
    fontFamily: "secondary",
    margin: "0.75em 0",
    fontSize: ["12px", "12px", "14px"],
    color: "#FFFFFF",
    textAlign: "center",
    a: {
      color: "#ffffff",
      textDecoration: "underline",
    },
  },
  footer: {
    flexGrow: 1,
    justifyContent: "flex-end",
    flexDirection: "column",
    span: {
      fontFamily: "secondary",
      margin: "15px 0",
      fontSize: "12px",
      color: "#FFFFFF",
      textAlign: "center",
    },
    button: {
      backgroundColor: "#FFF",
      fontFamily: "body",
      fontSize: ["36px", null, "50px"],
      padding: "0.5em 0",
      borderRadius: "100px",
      color: "#333",
      boxShadow: "0 0 10px 0 rgba(0,0,0,0.50)",
    },
  },
}
function Cart() {
  const {
    store: { isCartOpen, isNoteModalOpen },
    setIsCartOpen,
    toggleNoteModal,
  } = useContext(Context)
  const {
    checkout: { lineItems, webUrl, note },
    removeBundleFromCart,
  } = useContext(ShopifyContext)

  const cartRef = useRef()

  const data = useStaticQuery(graphql`
    query CartQuery {
      bundle: file(relativePath: { eq: "cart_image.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 500, quality: 99) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  const closeHandler = useCallback(
    e => {
      if (e.key === "Escape") setIsCartOpen(false)
    },
    [setIsCartOpen]
  )

  useEventListener("keydown", closeHandler)
  useClickObserver(cartRef, isCartOpen && !isNoteModalOpen, () =>
    setIsCartOpen(false)
  )

  const { transform } = useSpring({
    transform: isCartOpen ? "translate3d(0,0,0)" : `translate3d(100%,0,0)`,
  })

  const bundles = []

  lineItems
    .map(i => i.customAttributes[0].value)
    .filter(unique)
    .forEach(i => {
      bundles.push({
        bundleIndex: i,
        lineItems: [],
      })
    })

  lineItems.forEach(item => {
    const bundle = bundles.find(
      b => b.bundleIndex === item.customAttributes[0].value
    )

    if (bundle) {
      if (!bundle.bundleSize) bundle.bundleSize = item.customAttributes[1].value
      bundle.lineItems.push({
        title: item.title,
        quantity: item.quantity,
      })
    } else {
      return
    }
  })

  const handleNoteClick = e => {
    e.preventDefault()
    toggleNoteModal()
  }

  const noteMessage =
    note !== ""
      ? "Want to edit your gift note? "
      : "Want to add a gift note to your order? "

  return (
    <a.div
      id="cart"
      ref={cartRef}
      aria-hidden={!isCartOpen}
      sx={styles.container}
      style={{ transform: transform.interpolate(t => t) }}
    >
      <Flex sx={styles.header} as="header">
        <Text as="h2">{"Cart"}</Text>
        <Button
          tabIndex={isCartOpen ? 0 : -1}
          onClick={() => setIsCartOpen(false)}
        >
          {"[  X  ]"}
        </Button>
      </Flex>
      <Divider sx={{ color: "#FFF", opacity: "0.5", margin: "20px 0px 0px" }} />
      <Flex sx={styles.cartContent}>
        {bundles.map((bundle, index) => (
          <React.Fragment key={index}>
            <Flex key={bundle.bundleIndex} sx={styles.bundle}>
              <Image
                alt="Ice Cream Bundle"
                style={{ width: "50%", maxWidth: "215px" }}
                fluid={data.bundle.childImageSharp.fluid}
              />
              <Flex sx={styles.bundleDetails}>
                <Text as="h3" variant="secondary">{`Case ${index + 1}`}</Text>
                <Text as="p" variant="secondary">
                  {bundle.bundleSize === "4" ? "$55.00" : "$60.00"}
                </Text>
                <br />
                <ul sx={styles.bundleList}>
                  {bundle.lineItems.map(item => (
                    <li key={item.title}>
                      <Text as="span" variant="secondary">
                        {item.quantity} {item.title}
                      </Text>
                    </li>
                  ))}
                </ul>
                <Box>
                  <Button
                    variant="secondary"
                    onClick={() => removeBundleFromCart(bundle.bundleIndex)}
                  >
                    {"REMOVE"}
                  </Button>
                </Box>
              </Flex>
            </Flex>
            <Divider sx={{ color: "#FFF", opacity: "0.5", margin: "0px" }} />
          </React.Fragment>
        ))}
        {bundles.length > 0 ? (
          <Text sx={styles.note} as="p">
            {noteMessage}
            <a href="#note" onClick={handleNoteClick}>
              {"Click here"}
            </a>
            {"."}
          </Text>
        ) : null}
      </Flex>
      <Flex sx={styles.footer}>
        <Text as="span">{"Discounts can be added at checkout."}</Text>
        <a href={webUrl} alt="Checkout">
          <Button sx={{ width: "100%" }} tabIndex={isCartOpen ? 0 : -1}>
            {`Checkout`}
          </Button>
        </a>
      </Flex>
    </a.div>
  )
}

export default Cart
